import React, { useEffect, useId, useLayoutEffect, useState } from "react";
import { Link } from "react-router-dom";
import "../../pages/Login.css";

/* Nedaris N monogramı — logo ile birebir aynı geometri (public/brand/nedaris-mark.svg).
   Renk: kare currentColor, N ve bölme şeridi .n/.s sınıflarıyla CSS'ten (ters kontrast için .inv). */
export const NedarisMark: React.FC<{ className?: string }> = ({ className }) => (
  <svg viewBox="0 0 512 512" className={className} aria-hidden="true" focusable="false">
    <rect x="76" y="76" width="360" height="360" rx="8" fill="currentColor" />
    <rect className="s" x="252.5" y="76" width="7" height="360" />
    <g className="n">
      <rect x="163" y="148" width="37" height="216" />
      <rect x="312" y="148" width="37" height="216" />
      <polygon points="163,148 200,148 349,364 312,364" />
    </g>
  </svg>
);

/* ── Çözgü N ("Loom") ─────────────────────────────────────────────────
   Logonun N'si, jakar dokumadaki gibi kaldırılan çözgü (dikey) ipliklerinden
   oluşur ve sayfayı ikiye bölen omurga çizgisinin tam ortasında durur.
   37 iplik (x = 76 … 436, adım 10; x=256 tam omurga). Karenin içinde hepsi
   sönük; N geometrisiyle kırpılmış ikinci grup "kaldırılmış" (kalın, parlak).
   Sol yarı lacivert kapakta kâğıt ipliklerle, sağ yarı kâğıt tarafında lacivert
   ipliklerle render edilir (iki nested clipPath). Yalnız SVG + CSS; JS yok.
   Atkı (weft): N kırpması içinde 9 s'de bir süzülen ince yatay çizgi — kırpma
   grubun üstünde, hareket içteki rect'te (aksi hâlde kırpma da kayar). */
const XS = Array.from({ length: 37 }, (_, i) => 76 + i * 10);

const Loom: React.FC = () => {
  // useId çıktısındaki ":" / "«" karakterleri url(#…) içinde kırılır → temizle
  const id = useId().replace(/[^a-zA-Z0-9]/g, "");
  const threads = (y1: number, y2: number) =>
    XS.map((x, i) => (
      <line key={x} x1={x} y1={y1} x2={x} y2={y2} style={{ ["--i" as string]: i } as React.CSSProperties} />
    ));
  const half = (side: "l" | "r") => (
    <g className={`lg-loom-${side}`} clipPath={`url(#${id}-${side})`}>
      <rect className="lg-loom-frame" x="76" y="76" width="360" height="360" rx="8" vectorEffect="non-scaling-stroke" />
      <g className="lg-loom-warp">{threads(76, 436)}</g>
      <g className="lg-loom-lift" clipPath={`url(#${id}-n)`}>{threads(148, 364)}</g>
      <g clipPath={`url(#${id}-n)`}>
        <rect className="lg-loom-weft" x="76" y="0" width="360" height="2" />
      </g>
    </g>
  );
  return (
    <svg className="lg-loom" viewBox="0 0 512 512" aria-hidden="true" focusable="false">
      <defs>
        <clipPath id={`${id}-n`}>
          <rect x="163" y="148" width="37" height="216" />
          <rect x="312" y="148" width="37" height="216" />
          <polygon points="163,148 200,148 349,364 312,364" />
        </clipPath>
        <clipPath id={`${id}-l`}>
          <rect x="0" y="0" width="256" height="512" />
        </clipPath>
        <clipPath id={`${id}-r`}>
          <rect x="256" y="0" width="256" height="512" />
        </clipPath>
      </defs>
      {half("l")}
      {half("r")}
    </svg>
  );
};

/* Modül dizini — ikonlar uygulama menüsüyle (Sidebar) aynı çizgi dili */
const MI = (d: React.ReactNode) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">{d}</svg>
);
const MODULE_GROUPS: { title: string; items: { label: string; desc: string; icon: React.ReactNode }[] }[] = [
  {
    title: "Üretim",
    items: [
      { label: "Sipariş ve Atölye", desc: "kabulden teslime, kanban panosu", icon: MI(<><rect x="4" y="3" width="16" height="18" rx="2" /><path d="M8 8h8M8 12h8M8 16h5" /></>) },
      { label: "Model ve Maliyet", desc: "reçete oranlı maliyet, fiyat geçmişi", icon: MI(<><path d="M20.6 13.4 13.4 20.6a2 2 0 0 1-2.8 0l-7.2-7.2V3.4h10l7.2 7.2a2 2 0 0 1 0 2.8Z" /><circle cx="8" cy="8" r="1.6" /></>) },
      { label: "Stok ve Ürün Ağacı", desc: "hareketler, reçete, kritik seviye", icon: MI(<><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16Z" /><path d="M3.3 7 12 12l8.7-5M12 22V12" /></>) },
      { label: "Satın Alma ve Sevkiyat", desc: "mal kabul, irsaliye, teslimat", icon: MI(<><path d="M3 7h11v9H3zM14 10h4l3 3v3h-7z" /><circle cx="7" cy="18" r="1.6" /><circle cx="17" cy="18" r="1.6" /></>) },
    ],
  },
  {
    title: "Finans",
    items: [
      { label: "Cari Hesaplar", desc: "bakiye, tahsilat, vade takibi", icon: MI(<><rect x="2" y="5" width="20" height="14" rx="2" /><path d="M2 10h20M6 15h4" /></>) },
      { label: "Kasa, Banka, Çek/Senet", desc: "kasa defteri, virman, portföy", icon: MI(<><rect x="2" y="5" width="20" height="14" rx="2" /><path d="M6 10h6M6 14h9M16 9l2 2 3-3" /></>) },
      { label: "Mizan ve Nakit Akış", desc: "dönem mizanı, haftalık tahmin", icon: MI(<><path d="M12 3v18M4 7h16" /><path d="M6 7l-3 6a3 3 0 0 0 6 0zM18 7l-3 6a3 3 0 0 0 6 0z" /></>) },
      { label: "Kesilen Belgeler", desc: "e-fatura, e-irsaliye", icon: MI(<><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8Z" /><path d="M14 2v6h6" /><path d="M9 15l2 2 4-4" /></>) },
    ],
  },
  {
    title: "İnsan ve Zaman",
    items: [
      { label: "İnsan Kaynakları", desc: "personel, izin, giriş-çıkış", icon: MI(<><circle cx="9" cy="8" r="3.5" /><path d="M2.5 20a6.5 6.5 0 0 1 13 0" /><path d="M16 3.5a3.5 3.5 0 0 1 0 7M21.5 20a6.5 6.5 0 0 0-5-6.3" /></>) },
      { label: "Randevu ve Takvim", desc: "dış randevu, ekip takvimi", icon: MI(<><rect x="3" y="5" width="18" height="16" rx="2" /><path d="M3 10h18M8 3v4M16 3v4M8 14h3M13 14h3M8 17.5h3" /></>) },
    ],
  },
  {
    title: "Yönetim",
    items: [
      { label: "Dinamik Raporlar", desc: "pivot ve rapor tasarımcısı", icon: MI(<><path d="M3 3v18h18" /><path d="M7 14l4-4 3 3 5-6" /><path d="M19 7h.01" /></>) },
      { label: "Roller ve Yetkiler", desc: "ekran ve işlem düzeyinde izin", icon: MI(<><path d="M12 22s8-3.5 8-10V5l-8-3-8 3v7c0 6.5 8 10 8 10Z" /><path d="M9 11.5l2 2 4-4" /></>) },
    ],
  },
];

interface Props {
  title: string;
  subtitle?: string;
  /** Kart içeriği (form) */
  children: React.ReactNode;
  /** Kartın altındaki dönüş bağlantısı */
  backTo?: { to: string; label: string };
}

const INTRO_KEY = "nedaris.auth.intro";

/**
 * Oturum dışı sayfaların (giriş, şifremi unuttum, şifre sıfırlama) ortak kabuğu.
 * Solda lacivert "kapak" (masthead, manşet, dek, dizin, künye), sağda kâğıt üstünde form kartı;
 * ikisini logodaki bölme çizgisi (omurga) ayırır, dev Çözgü N omurganın üstünde dokunur.
 * Açılış koreografisi oturumda BİR kez oynar (sessionStorage); sonraki yüklemeler 250 ms solma.
 * Görsel dil Login.css'ten gelir — tek kaynak.
 */
const AuthShell: React.FC<Props> = ({ title, subtitle, children, backTo }) => {
  const [quiet] = useState(() => {
    try { return sessionStorage.getItem(INTRO_KEY) === "1"; } catch { return false; }
  });
  useEffect(() => {
    try { sessionStorage.setItem(INTRO_KEY, "1"); } catch { /* gizli mod vb. — intro her seferinde oynar */ }
  }, []);
  useEffect(() => {
    const prev = document.title;
    document.title = `${title} · Nedaris`;
    return () => { document.title = prev; };
  }, [title]);
  // Uygulamanın yoğunluk ayarı (html[data-density] → %75/%85) veri ekranları içindir; giriş kabuğu
  // 16 px tabanlı tasarlandı. Kabuk açıkken kök boyutunu sabitle, kapanınca kullanıcının ayarına dön.
  useLayoutEffect(() => {
    const html = document.documentElement;
    const prev = html.style.fontSize;
    html.style.fontSize = "100%";
    return () => { html.style.fontSize = prev; };
  }, []);

  return (
    <div className={`lg-root${quiet ? " is-quiet" : ""}`}>
      <Loom />

      {/* ── Sol: kapak ── */}
      <aside className="lg-brand">
        <header className="lg-masthead">
          <NedarisMark className="lg-brand-mark inv" />
          <div className="lg-masthead-text">
            <span className="lg-wordmark">NEDARIS</span>
            <span className="lg-kicker">Üretim Yönetim Sistemi</span>
          </div>
        </header>

        <div className="lg-brand-main">
          <p className="lg-headline">
            <span className="lg-headline-a">Her iplik</span>
            <span className="lg-headline-b">hesapta.</span>
          </p>
          <p className="lg-lede">
            Kabulden sevkiyata her adım kayıt altında; yetkiler ekran ve işlem düzeyinde tanımlı.
          </p>
          <div className="lg-modules" aria-label="Modüller">
            {MODULE_GROUPS.map((g) => (
              <section key={g.title} className="lg-mod-group">
                <p className="lg-mod-title">{g.title}</p>
                <ul className="lg-mod-list">
                  {g.items.map((it) => (
                    <li key={it.label}>
                      <span className="lg-mod-icon">{it.icon}</span>
                      <span className="lg-mod-text"><strong>{it.label}</strong><em>{it.desc}</em></span>
                    </li>
                  ))}
                </ul>
              </section>
            ))}
          </div>
        </div>

        <footer className="lg-brand-foot">
          <a className="lg-site-link" href="https://nedaris.com" target="_blank" rel="noopener noreferrer">
            nedaris.com
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M7 17L17 7M9 7h8v8" />
            </svg>
          </a>
          <span className="lg-copyright">© 2026 Nedaris</span>
        </footer>
      </aside>

      {/* ── Sağ: form ── */}
      <main className="lg-form-side">
        {/* Mobil: omurga çizgisi tepeden iner, monogramın arkasından geçip kartın üst kenarında biter */}
        <div className="lg-mobile-brand">
          <NedarisMark className="lg-brand-mark" />
          <span className="lg-wordmark">NEDARIS</span>
          <span className="lg-kicker">Üretim Yönetim Sistemi</span>
        </div>

        <div className="lg-form-card">
          <h1 className="lg-form-title">{title}</h1>
          {subtitle && <p className="lg-form-sub">{subtitle}</p>}

          {children}

          {backTo && (
            <div className="lg-back">
              <Link to={backTo.to}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M19 12H5M11 18l-6-6 6-6" />
                </svg>
                {backTo.label}
              </Link>
            </div>
          )}

        </div>

        <div className="lg-mobile-foot">
          <a href="https://nedaris.com" target="_blank" rel="noopener noreferrer">nedaris.com</a>
          <span>© 2026 Nedaris</span>
        </div>
      </main>
    </div>
  );
};

export default AuthShell;
